import { Link } from "react-router-dom";
import { ShieldCheck, School, GraduationCap, ArrowRight, Sparkles } from "lucide-react";

// Uploaded SAARTHI logo (public CDN — no bundling cost).
const LOGO =
  "/logo-mind-mantra.svg";

const ROLES = [
  {
    to: "/admin/login",
    title: "Super Admin",
    blurb: "Manage schools, categories, questions, coupons & analytics.",
    testid: "home-admin-login-link",
    Icon: ShieldCheck,
    accent: "from-indigo-500 to-blue-500",
    ring: "hover:ring-indigo-300/60",
  },
  {
    to: "/school/login",
    title: "School Admin",
    blurb: "Onboard students, assign tests and view school reports.",
    testid: "home-school-login-link",
    Icon: School,
    accent: "from-violet-500 to-indigo-500",
    ring: "hover:ring-violet-300/60",
  },
  {
    to: "/student/login",
    title: "Student",
    blurb: "Take your assigned assessments and download your report.",
    testid: "home-student-login-link",
    Icon: GraduationCap,
    accent: "from-fuchsia-500 to-violet-500",
    ring: "hover:ring-fuchsia-300/60",
  },
];

export default function Home() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-white text-slate-900">
      {/* ---------- Animated background (pure CSS, GPU friendly) ---------- */}
      <style>{`
        @keyframes saarthi-drift  { 0%,100% { transform: translate3d(0,0,0) scale(1);} 50% { transform: translate3d(30px,-40px,0) scale(1.08);} }
        @keyframes saarthi-drift2 { 0%,100% { transform: translate3d(0,0,0) scale(1);} 50% { transform: translate3d(-40px,30px,0) scale(1.05);} }
        @keyframes saarthi-float  { 0%,100% { transform: translateY(0);} 50% { transform: translateY(-6px);} }
        @keyframes saarthi-ray    { 0% { transform: translateX(-40%) rotate(15deg); opacity:.35;} 100% { transform: translateX(160%) rotate(15deg); opacity:0;} }
        @keyframes saarthi-fade-up{ 0% { opacity:0; transform: translateY(14px);} 100% { opacity:1; transform: translateY(0);} }
        .saarthi-fade { opacity: 0; animation: saarthi-fade-up .7s cubic-bezier(.2,.7,.2,1) forwards; }
        @media (prefers-reduced-motion: reduce) {
          .saarthi-fade { animation: none; opacity: 1; transform: none; }
          .saarthi-anim { animation: none !important; }
        }
      `}</style>

      {/* Base bright gradient */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-30 bg-[linear-gradient(180deg,#ffffff_0%,#f6f2ff_40%,#eef1ff_75%,#e6ecff_100%)]"
      />

      {/* Two soft floating gradient blobs */}
      <div
        aria-hidden="true"
        className="saarthi-anim absolute -top-40 -left-32 w-[42rem] h-[42rem] rounded-full -z-20 blur-3xl opacity-70"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, #c7d2fe 0%, transparent 60%)",
          animation: "saarthi-drift 14s ease-in-out infinite",
        }}
      />
      <div
        aria-hidden="true"
        className="saarthi-anim absolute -bottom-48 -right-40 w-[48rem] h-[48rem] rounded-full -z-20 blur-3xl opacity-70"
        style={{
          background:
            "radial-gradient(circle at 70% 70%, #ddd6fe 0%, transparent 60%)",
          animation: "saarthi-drift2 18s ease-in-out infinite",
        }}
      />

      {/* Diagonal light ray sweeping slowly */}
      <div
        aria-hidden="true"
        className="saarthi-anim absolute top-0 left-0 h-full w-[45%] -z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(115deg, transparent 30%, rgba(255,255,255,.65) 50%, transparent 70%)",
          filter: "blur(30px)",
          animation: "saarthi-ray 12s linear infinite",
        }}
      />

      {/* Floating glowing particles (12 dots) */}
      <div aria-hidden="true" className="absolute inset-0 -z-10 pointer-events-none">
        {Array.from({ length: 12 }).map((_, i) => {
          const size = 4 + (i % 4) * 3;
          const left = (i * 83) % 100;
          const top = (i * 47) % 100;
          const delay = (i * 0.6).toFixed(2);
          const dur = 6 + (i % 5);
          return (
            <span
              key={i}
              className="saarthi-anim absolute rounded-full"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${size}px`,
                height: `${size}px`,
                background:
                  "radial-gradient(circle, rgba(129,140,248,.9) 0%, rgba(129,140,248,0) 70%)",
                animation: `saarthi-float ${dur}s ease-in-out ${delay}s infinite`,
              }}
            />
          );
        })}
      </div>

      {/* ---------- Hero ---------- */}
      <main className="relative max-w-6xl mx-auto px-6 pt-14 pb-10 sm:pt-20 sm:pb-14">
        <header
          className="flex flex-col items-center text-center saarthi-fade"
          style={{ animationDelay: "80ms" }}
        >
          <img
            src={LOGO}
            alt="SAARTHI"
            className="h-16 w-auto sm:h-20 drop-shadow-[0_6px_24px_rgba(99,102,241,0.25)]"
            loading="eager"
            decoding="async"
          />
          <span
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-white/70 backdrop-blur px-3 py-1 text-[11px] font-medium text-indigo-700"
          >
            <Sparkles className="h-3 w-3" aria-hidden="true" />
            Scientifically designed psychometric & career assessments
          </span>
        </header>

        <section className="mt-8 max-w-3xl mx-auto text-center">
          <h1
            className="saarthi-fade text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900"
            style={{ animationDelay: "220ms" }}
          >
            Understand Today.{" "}
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
              Empower Tomorrow.
            </span>
          </h1>
          <p
            className="saarthi-fade mt-4 text-base sm:text-lg leading-relaxed text-slate-600"
            style={{ animationDelay: "360ms" }}
          >
            Scientifically designed psychometric and career assessments that
            help schools understand every student&apos;s strengths, personality,
            interests and future career pathways.
          </p>
        </section>

        {/* ---------- Role cards (glassmorphism) ---------- */}
        <section className="mt-12 sm:mt-16 grid sm:grid-cols-3 gap-5">
          {ROLES.map((r, i) => (
            <Link
              key={r.to}
              to={r.to}
              data-testid={r.testid}
              className={
                "saarthi-fade group relative rounded-2xl border border-white/60 bg-white/60 " +
                "backdrop-blur-xl p-6 shadow-[0_10px_40px_-15px_rgba(79,70,229,0.25)] " +
                "ring-1 ring-transparent transition-all duration-300 " +
                "hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(79,70,229,0.35)] hover:ring-4 " +
                r.ring
              }
              style={{ animationDelay: `${520 + i * 120}ms` }}
            >
              {/* Gradient accent bar */}
              <div
                aria-hidden="true"
                className={`absolute inset-x-6 top-0 h-[3px] rounded-full bg-gradient-to-r ${r.accent} opacity-70`}
              />

              <div className="flex items-start justify-between">
                <div
                  className={`inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${r.accent} text-white shadow-lg shadow-indigo-500/20 transition-transform duration-300 group-hover:scale-105`}
                >
                  <r.Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <ArrowRight
                  className="h-4 w-4 text-indigo-500 opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0"
                  aria-hidden="true"
                />
              </div>

              <h2 className="mt-4 text-lg font-semibold text-slate-900">
                {r.title}
              </h2>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                {r.blurb}
              </p>

              {/* Button-style CTA (still same navigation) */}
              <span
                className={
                  "mt-5 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium text-white " +
                  `bg-gradient-to-r ${r.accent} shadow-md shadow-indigo-500/20 transition-all duration-300 group-hover:shadow-lg`
                }
                aria-hidden="true"
              >
                Continue
                <ArrowRight className="h-3 w-3" />
              </span>

              {/* Idle glow ring */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background:
                    "radial-gradient(400px circle at var(--x,50%) var(--y,50%), rgba(129,140,248,.18), transparent 40%)",
                }}
              />
            </Link>
          ))}
        </section>

        {/* Trust strip */}
        <p
          className="saarthi-fade mt-10 text-center text-xs text-slate-500"
          style={{ animationDelay: "980ms" }}
        >
          Trust · Scientific credibility · Student growth · Future readiness
        </p>
      </main>

      {/* ---------- Footer ---------- */}
      <footer
        className="saarthi-fade relative border-t border-indigo-100/70 bg-white/60 backdrop-blur"
        style={{ animationDelay: "1100ms" }}
      >
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <span>
            A Product of{" "}
            <span className="font-medium text-slate-700">
              Imagenators Global Technology Private Limited
            </span>
          </span>
          <span>For guidance only · Non-clinical assessment platform</span>
        </div>
      </footer>
    </div>
  );
}
